"use client";
import { useState } from 'react';
import { message, Upload, Modal, Progress } from '@basic-components';
import { InboxOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { ImportExportProps } from './types';
import { AuthButton } from "@general-components";
import { useMutationFormData, usePost } from "@hooks/useRequest";
import { MappingConfirmModal } from './MappingConfirmModal';

const { Dragger } = Upload;

export const ImportExport = ({
  exportUrl,
  importUrl,
  exportParams = {},
  onImportSuccess,
  exportButtonText = '导出',
  importButtonText = '导入',
  templateButtonText = '下载模板',
  templateType = '',
  authKey,
  smartImport = false,
}: ImportExportProps) => {
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mappingConfirmVisible, setMappingConfirmVisible] = useState(false);

  const url = `/api/export-excel?exportUrl=${exportUrl}`;
  // 处理导出
  const handleExport = async () => {
    try {
      // 使用 fetch API 直接处理导出
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
        },
        body: JSON.stringify(exportParams),
        credentials: 'include'
      });


      if (!response.ok) {
        throw new Error(`请求失败，状态码: ${response.status}`);
      }

      const blob = await response.blob();

      // 从响应头获取文件名
      const contentDisposition = response.headers.get('content-disposition');
      let fileName = '导出数据.xlsx';
      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*=([^']*'')?([^;]+)/);
        if (filenameStarMatch && filenameStarMatch[2]) {
          fileName = decodeURIComponent(filenameStarMatch[2]);
        } else {
          const filenameMatch = contentDisposition.match(/filename[^*=;\n]*=\s*"?([^";\n]*)"?/);
          if (filenameMatch && filenameMatch[1]) {
            fileName = filenameMatch[1];
          }
        }
      }

      // 创建下载链接并触发下载
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);

      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请重试');
    }
  };

  const { data } = usePost(
    "/manage/exhibition/operation/v1/importTemplate",
    {
      type: templateType
    }
  );

  // 处理模板下载
  const handleTemplateDownload = async () => {
    try {
      if (!data) {
        message.error('未获取到模板数据');
        return;
      }
      const fileName = '模版.xlsx';

      const a = document.createElement('a');
      a.href = data;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (error) {
      console.error('模板下载失败:', error);
      message.error('模板下载失败，请重试');
    }
  };

  const params = new URLSearchParams();
  params.append("importUrl", importUrl || '');
  const queryString = params.toString();

  const { mutateAsync: uploadAsync } = useMutationFormData(`/import-excel?${queryString}`);

  // 处理导入
  const handleImport = async (file: any) => {
    if (!importUrl) {
      message.error('未配置导入接口');
      return false;
    }

    // 智能导入模式：先显示映射确认
    if (smartImport) {
      setSelectedFile(file);
      setImportModalVisible(false);
      setMappingConfirmVisible(true);
      return false;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    const res = await uploadAsync(formData);
    if (res.code === 200) {
      message.success('导入成功');
      setImportModalVisible(false);
      onImportSuccess?.();
    } else {
      message.error(res.message || '导入失败');
      setUploading(false);
    }
  };

  // 映射确认成功回调
  const handleMappingConfirmSuccess = () => {
    setSelectedFile(null);
    setMappingConfirmVisible(false);
    onImportSuccess?.();
  };

  // 取消映射确认回调
  const handleMappingConfirmCancel = () => {
    setSelectedFile(null);
    setMappingConfirmVisible(false);
  };

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx,.xls',
    showUploadList: false,
    beforeUpload: handleImport,
  };

  return (
    <>
     <AuthButton
        icon={<UploadOutlined />}
        onClick={() => setImportModalVisible(true)}
        authKey={authKey+':import'}
      >
        {importButtonText}
      </AuthButton>

      <AuthButton
        icon={<DownloadOutlined />}
        onClick={handleExport}
        authKey={authKey+':export'}
      >
        {exportButtonText}
      </AuthButton>

     {templateType && <AuthButton
        icon={<DownloadOutlined />}
        onClick={handleTemplateDownload}
        authKey={authKey+':import'}
      >
        {templateButtonText}
      </AuthButton>}

      <Modal
        title="导入数据"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
      >
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
          <p className="ant-upload-hint">
            支持 .xlsx, .xls 格式文件
          </p>
        </Dragger>

        {uploading && (
          <div className="mt-4">
            <Progress percent={uploadProgress} status="active" />
          </div>
        )}
      </Modal>

      {/* 智能导入映射确认弹窗 */}
      {importUrl && (
        <MappingConfirmModal
          visible={mappingConfirmVisible}
          file={selectedFile}
          importUrl={importUrl}
          onCancel={handleMappingConfirmCancel}
          onConfirm={handleMappingConfirmSuccess}
          onSuccess={handleMappingConfirmSuccess}
        />
      )}
    </>
  );
};

export default ImportExport;
